import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { Table } from "antd";

import {
    IStoreScoreRecord,
    IRetailMatchTradeAreaBlockGroup,
    IRetailMatchJobResult,
    IRetailMatchOverview,
    IRetailMatchChartData,
} from "../../../../Models/IRetailMatch";
import { getRetailMatchIndexColor } from "../../../../Util/ColorUtil";
import { RetailMatchChartIndexPill } from "../Styled/RetailMatchChartIndexPill";
import {
    ReportSectionHeader,
    ReportH4,
    ReportSectionNumber,
    useWindowResize,
    AnimatedReportCard,
    ReportH1,
    Button,
    ReportScrollableSection,
    ReportMap,
    GoogleMapsMarker,
    BuxTooltip,
} from "../../../Common/";
import {
    getTradeAreaPolygonsFromBlockGroups,
    setMapCenter,
    setMapMarkers,
    createNewGoogleMap,
    cleanMap
} from "../../../../Actions/ReportMapActions";
import { deviceWidth, device } from "../../../../globalStyles";
import { MapLayers } from "../../../../Util/MapLayers/MapLayers";
import { MapTypesEnum } from "../../../../Enums/MapTypesEnum";
import { RetailMatchMapLegend } from "./Map/RetailMatchMapLegend";
import { BrandLogoSmall } from "./BrandLogo/BrandLogoSmall";
import { createBrandMapMarker } from "./Map/GoogleMapBrandMarker";
import { titleCase } from "../Logic/SharedMethods";
import { IPolygon } from "../../../../Models/IPolygon";
import { ReportMapWrapper } from "../../../Common/Map/Styles/MapStyles";
import { RenderType } from "../../../../Enums/RenderType";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import {
    isOutputSelected,
    registerElement,
    registerRef,
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import { LocationsAnalyzedSharing } from "./LocationsAnalyzedSharing";
import { IBrand } from "../../../../Models/IBrand";
import { brandOverviewRecord } from "../../../../Util/RetailMatch/RetailMatchUtil";
import { getMapInfoWindowContentString } from "./Map/MapUtil";
import { brandStores } from "../Util/RetailMatchUtil";
import { retailMatchLocationsAnalyzedMapDataSelector } from "../../../../Reducers/Selectors/RetailMatchSelectors";

interface RetailMatchLocationsAnalyzedProps {
  jobResult: IRetailMatchJobResult;
  brand: IBrand;
  benchmarkRecord: IStoreScoreRecord;
  renderType: RenderType;
  app: DigitalShareAppType;
}

//https://loading.io/icon/4j5ilt
const loadingIcon
  = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAC8AAAAvCAYAAABzJ5OsAAAD2UlEQVRoQ+2YQWhTWRSG/2OkVhdtgwuVyYuRQYNQxwrSQhdWkChFcfpA3NYiKrixLsTZTcWFMC6msxEFxboWfV0oRYNgXRQaRFsqSApiyHtSXUhMFrZTTI+c1/fGUCy97742TjF31ZRzzv3uf//ce24Iq3jQKmZHDf5H7V5N+ZryGgr8PLYZjMePEdCsIZJSCs3NTRx1nHtKwYD6UWnF46eJ+YZqYe045lNdjnNTJV/ZNoOx2BUQ/aFSNEwME/1l5vMXVWoow0uxQcO4TsBxBqJgniSAGGCViRaLcWsQbQdQYOa7puOcUa0XCN4vOmgYzMzjRLRbdaLF4hh4Q8CvXbYdmCVwgrcDonYRQGNYeAJKDDRUDd4yjKcEdDAg1tmhuwA/n4Fh07b3B62jpbwVj/cS898AngHYF3TSing3n4nOm/l8f9A6evCJRILK5bdinU0HDjyP1NdHgk5cnpkpf3jyZK9YjyORbWYulwtaQwteJrEMY4CA7r3Xrg3/cuRIR9CJ3z14MPz87Fmx3h3Ttk8EzZd4ffh59cdEucPZ7OTa9euVvf9lenryYTIp8UWORFp0VA8F76rveb+usbGYymQ+qCxAwNOtrZtmi8VGBnpM2x7QUT00fKV9ZAEdQ0PZDbFY62Iwnx0nM9zZmfTAte3i19e2TSWgFYv1E9E5+d+WgwfHWq5epbpo9L8LbLZQGB+7cIGnHj9ukRgmumTm8326ii8rvLsDsdh+AgZAtFU+p0ZGMrILona6vd3dDTnPwdxnOs7TsODLYpuFEFY83kfMf25sb39UH42umykU/v04MnKIgP7fbfv8ckCvjPJEtwEkPJUzBIjiowDavAlzzNzzv1LeV9uFloZtzRoGs+tvdxCN8dwc+Y3ccu1C6C+sf1l5DdbrCpW/55BRAnZKIwZgoMu2e8LYKBS8ZRgnCLjtghNNgTm5JAxRlpi3yAJ+2Dlvzd+wLwE0Be4uibLeQj9xJLKn6jesbxc5/qQ9XlLxBQF+XtV7G0/1t3UNDaXOiQkGUfBHCXNxaNcumi2VGqraVfo9zW+XLz/b1t2t3c/b9++PvujtbatuP++9pIJ2kwut9WV6OvswmUxW9SUlD3DXMq9eyZEXagw1N5fEOlV7wwr85lRqvO3WrdC/HoyePDn+Pp3evaLwViLRROWyXP9NIGpyO8hUKpTqkjyVTs/XYP4EQI7OHjOXk7+XHMqXVGXbu2TVEAHM/I/pOL0qJdThv/1ioFJXOybIyaMMLzRyRLq2WanBnAvyLAwEv1LMunVr8LrKhc2rKR9WQd38mvK6yoXNW9XKfwUU75E/M/OAiwAAAABJRU5ErkJggg==";
const defaultGoogleMarker
  = "https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2.png";

export const StyledTable = styled(Table)`
  margin-bottom: 40px;
  .ant-table {
    font-size: 13px;
  }
  .ant-table-thead,
  .ant-table-tbody {
    > tr {
      > th,
      > td {
        padding: 13px 3px;
        color: black;
        background-color: #fafafa;
        vertical-align: middle;
        &:first-child {
          padding-left: 16px;
        }
        &:last-child {
          padding-right: 16px;
        }
      }
      > th {
        font-weight: 400;
        .ant-table-column-sorter {
          .ant-table-column-sorter-inner {
            margin-left: 0;
          }
        }
      }
    }
  }
`;

const DynamicNAText = styled.div`
  &.na {
    color: #00000085;
  }
`;

const TableTitleSpan = styled.span`
  margin-left: 14px;
  font-size: 20px;
`;

const TableTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > div {
    color: #6d7278;
    ${device.desktop} {
      display: none;
    }
  }
`;

const addFullAddr = (array: IStoreScoreRecord[]): IStoreScoreRecord[] => {
    array.forEach((item: IStoreScoreRecord) => {
        item.fullAddr = titleCase(
            `${item.addr} ${item.city}, ${item.st} ${item.zip}`
        );
    });
    return array;
};

export const RetailMatchLocationsAnalyzed: FunctionComponent<RetailMatchLocationsAnalyzedProps>
  = (props: RetailMatchLocationsAnalyzedProps) => {
      const { jobResult, brand, benchmarkRecord } = props;
      const chartData: IRetailMatchChartData = jobResult.chartData;
      let storeScores: IStoreScoreRecord[] = [...chartData.storeScores];

      storeScores = addFullAddr(storeScores);
      storeScores = brandStores(storeScores, brand.brandId);

      const brandLogo: string = brand.logoPath;
      const brandName: string = brand.brandName;

      const windowSize = useWindowResize();
      const dispatch = useDispatch();
      const ref = useRef();

      const {
          googleMap,
          currentMapMarkers,
          buxtonDeck,
          geographies
      } = useSelector(retailMatchLocationsAnalyzedMapDataSelector);
      const [selectedMarker, setSelectedMarker] = useState<any>();

      const overviewRecord: IRetailMatchOverview = brandOverviewRecord(
          props.brand.brandId,
          props.jobResult.chartData.overview
      );

      /** effects */
      useEffect(() => {
          return () => {
              setSelectedMarker(null);
          };
      }, []);

      useEffect(() => {
          if (googleMap !== null && googleMap !== undefined) {
              refreshMapMarkers();
              currentMapMarkers.forEach((m) => {
                  if (m.getAnimation() !== null) {
                      m.setAnimation(null);
                  }
              });
          }
      }, [brand, googleMap]);

      useEffect(() => {
          if (storeScores) {
              refreshMapMarkers();
          }
      }, [
          benchmarkRecord.addr,
          benchmarkRecord.city,
          benchmarkRecord.st,
          benchmarkRecord.zip,
          brandLogo
      ]);

      const refreshMapMarkers = ():void => {
          dispatch(cleanMap());
          const mapMarkers: google.maps.Marker[] = [];
          /** remove current map markers */
          for (const marker of currentMapMarkers) {
              marker.setMap(null);
          }

          //create benchmark marker
          const benchmarkMarker = new google.maps.Marker({
              map: googleMap,
              draggable: false,
              animation: google.maps.Animation.DROP,
              position: { lat: benchmarkRecord.lat, lng: benchmarkRecord.lon },
          });

          dispatch(setMapCenter(benchmarkMarker.getPosition()));
          loadTradeAreaBlockGroups("-1");

          //create sample site map markers
          for (let i = 0; i < storeScores.length; i++) {
              const store = storeScores[i];

              const brandMarker = createBrandMapMarker({
                  map: googleMap,
                  latlng: { lat: store.lat, lng: store.lon },
                  imgSrc: brandLogo,
                  animation: google.maps.Animation.DROP,
                  markerId: `rm-brand-marker-${i}`,
              });

              mapMarkers.push(brandMarker);
          }

          //add benchmark marker to markers
          mapMarkers.push(benchmarkMarker);
          dispatch(setMapMarkers(mapMarkers));

          //assign click event actions to all markers
          for (let i = 0; i < mapMarkers.length; i++) {
              let infoWindow: google.maps.InfoWindow;

              if (i < mapMarkers.length - 1) {
                  //If the marker is one of the brand stores
                  infoWindow = new google.maps.InfoWindow({
                      content: getMapInfoWindowContentString(
                          storeScores[i].addr,
                          storeScores[i].city,
                          storeScores[i].st
                      ),
                  });
              } else {
                  //If it's the benchmark site
                  infoWindow = new google.maps.InfoWindow({
                      content: getMapInfoWindowContentString(
                          benchmarkRecord.addr,
                          benchmarkRecord.city,
                          benchmarkRecord.st
                      ),
                  });
              }

              mapMarkers[i].addListener("mouseover", () =>
                  infoWindow.open(googleMap, mapMarkers[i])
              );
              mapMarkers[i].addListener("mouseout", () => infoWindow.close());

              mapMarkers[i].addListener("click", () => {
                  const tableRow: IStoreScoreRecord = storeScores[i];
                  const isBenchmarkSite = !tableRow;
                  const sid: string = isBenchmarkSite
                      ? "-1"
                      : tableRow.sid;

                  setSelectedMarker(mapMarkers[i]);
                  mapMarkers[i].setIcon(loadingIcon);
                  loadTradeAreaBlockGroups(sid).then(() =>
                      isBenchmarkSite
                          ? mapMarkers[i].setIcon(defaultGoogleMarker)
                          : mapMarkers[i].setIcon(brandLogo)
                  );

                  mapMarkers.forEach((m) => {
                      if (m.getAnimation() !== null) {
                          m.setAnimation(null);
                      }
                  });

                  if (mapMarkers[i].getAnimation() !== null) {
                      mapMarkers[i].setAnimation(null);
                  } else {
                      mapMarkers[i].setAnimation(google.maps.Animation.BOUNCE);
                  }
              });
          }
      };

      const vCntIdxHasValue = (tableData: IStoreScoreRecord[]): boolean => {
          return tableData?.some((store: IStoreScoreRecord) => store.vCntIdx !== 0);
      };

      const mobileOnlyTableColumnsBeforeData: any[] = [
          {
              title: "Address",
              dataIndex: "fullAddr",
              width: "147px",
              textWrap: "word-break",
          },
      ];

      const desktopOnlyTableColumnsBeforeData: any[] = [
          {
              title: "Address",
              dataIndex: "addr",
              width: "118px",
              textWrap: "word-break",
          },
          {
              title: "City",
              dataIndex: "city",
              width: "105px",
              textWrap: "word-break",
          },
          {
              title: "State",
              dataIndex: "st",
              width: "40px",
          },
          {
              title: "Zip",
              dataIndex: "zip",
              width: "80px",
          },
      ];

      const desktopOnlyTableColumnsAfterData = [
          {
              title: "View",
              dataIndex: "view",
              width: "80px",
              align: "center",
              render: (text: string, store: IStoreScoreRecord, index: number) => (
                  <Button
                      style={{ padding: "0 17px", color: "black", height: "40px" }}
                      clickCallback={() => handleViewLocation(store)}
                      title="Map View"
                  ></Button>
              ),
          },
      ];

      const anySizeTableColumns: any[] = [
          {
              title: "Ideal Customer Count",
              dataIndex: "ccCntIdx",
              width: "90px",
              align: "center",
              sorter: (a: IStoreScoreRecord, b: IStoreScoreRecord) =>
                  a != null && b != null && b.ccCntIdx < a.ccCntIdx ? 1 : -1,
              render: (ccCntIdx: number) => (
                  <div style={{ display: "inline-block" }}>
                      <RetailMatchChartIndexPill
                          style={{ background: getRetailMatchIndexColor(ccCntIdx) }}
                      >
                          {ccCntIdx}
                      </RetailMatchChartIndexPill>
                  </div>
              ),
          },
          {
              title: "Ideal Customer Ratio",
              dataIndex: "ccPerHHCntIdx",
              width: "90px",
              align: "center",
              sorter: (a: IStoreScoreRecord, b: IStoreScoreRecord) =>
                  a != null && b != null && b.ccPerHHCntIdx < a.ccPerHHCntIdx ? 1 : -1,
              render: (ccPerHHCntIdx: number) => (
                  <div style={{ display: "inline-block" }}>
                      <RetailMatchChartIndexPill
                          style={{ background: getRetailMatchIndexColor(ccPerHHCntIdx) }}
                      >
                          {ccPerHHCntIdx}
                      </RetailMatchChartIndexPill>
                  </div>
              ),
          },
          {
              title: "Household Similarity",
              dataIndex: "ccCorrIdx",
              width: "88px",
              align: "center",
              sorter: (a: IStoreScoreRecord, b: IStoreScoreRecord) =>
                  a != null && b != null && b.ccCorrIdx < a.ccCorrIdx ? 1 : -1,
              render: (ccCorrIdx: number) => (
                  <div style={{ display: "inline-block" }}>
                      <RetailMatchChartIndexPill
                          style={{ background: getRetailMatchIndexColor(ccCorrIdx) }}
                      >
                          {ccCorrIdx}
                      </RetailMatchChartIndexPill>
                  </div>
              ),
          },
      ];

      const anySizeSingleBenchmarkTableColumns: any[] = [
          {
              title: (
                  <DynamicNAText
                      className={vCntIdxHasValue([benchmarkRecord]) ? null : "na"}
                  >
            Mobile Device Volume{" "}
                      {vCntIdxHasValue([benchmarkRecord]) && (
                          <BuxTooltip
                              content={`This index compares the count of mobile devices observed at each location relative to the average count observed at the ${
                                  overviewRecord?.sampleCount || 0
                              } ${brandName} locations.`}
                              trigger="click"
                              allowClickThrough={false}
                              iconSize={12}
                              style={{ verticalAlign: "middle" }}
                          />
                      )}
                  </DynamicNAText>
              ),
              dataIndex: "vCntIdx",
              width: "87px",
              align: "center",
              // ERY (10/29/2020): this isn't in design and the other sorting functions are, so I think this is on purpose, but I'm leaving it as is for now.
              // sorter: (a: IStoreScoreRecord, b: IStoreScoreRecord) =>
              //   a != null && b != null && b.vCntIdx < a.vCntIdx ? 1 : -1,
              render: (vCntIdx: number) => (
                  <DynamicNAText
                      className={vCntIdxHasValue([benchmarkRecord]) ? null : "na"}
                  >
                      {vCntIdx === 0 ? "N/A" : vCntIdx}
                  </DynamicNAText>
              ),
          },
      ];

      const anySizeBenchmarkSetTableColumns: any[] = [
          {
              title: (
                  <DynamicNAText className={vCntIdxHasValue(storeScores) ? null : "na"}>
            Mobile Device Volume{" "}
                      {vCntIdxHasValue(storeScores) && (
                          <BuxTooltip
                              content={`This index compares the count of mobile devices observed at each location relative to the average count observed at the ${
                                  overviewRecord?.sampleCount || 0
                              } ${brandName} locations.`}
                              trigger="click"
                              allowClickThrough={false}
                              iconSize={12}
                              style={{ verticalAlign: "middle" }}
                          />
                      )}
                  </DynamicNAText>
              ),
              dataIndex: "vCntIdx",
              width: "88px",
              align: "center",
              // ERY (10/29/2020): this isn't in design and the other sorting functions are, so I think this is on purpose, but I'm leaving it as is for now.
              sorter: (a: IStoreScoreRecord, b: IStoreScoreRecord) =>
                  a != null && b != null && b.vCntIdx < a.vCntIdx ? 1 : -1,
              render: (vCntIdx: number) => (
                  <DynamicNAText className={vCntIdxHasValue(storeScores) ? null : "na"}>
                      {vCntIdx === 0 ? "N/A" : vCntIdx}
                  </DynamicNAText>
              ),
          },
      ];

      // To avoid reusing table columns across sets...concat them
      const mobileSingleBenchmarkTableColumns = mobileOnlyTableColumnsBeforeData
          .concat(anySizeTableColumns)
          .concat(anySizeSingleBenchmarkTableColumns);
      const desktopSingleBenchmarkTableColumns = desktopOnlyTableColumnsBeforeData
          .concat(anySizeTableColumns)
          .concat(anySizeSingleBenchmarkTableColumns)
          .concat(desktopOnlyTableColumnsAfterData);
      const mobileBenchmarkSetTableColumns = mobileOnlyTableColumnsBeforeData
          .concat(anySizeTableColumns)
          .concat(anySizeBenchmarkSetTableColumns);
      const desktopBenchmarkSetTableColumns = desktopOnlyTableColumnsBeforeData
          .concat(anySizeTableColumns)
          .concat(anySizeBenchmarkSetTableColumns)
          .concat(desktopOnlyTableColumnsAfterData);

      const loadBlockGroupMapLayers = (
          blockgroups: IRetailMatchTradeAreaBlockGroup[],
          polygons: IPolygon[],
          isBenchmark: boolean
      ) => {
          const getVolumePercent = (externalID: string): number => {
              const bgMatch: IRetailMatchTradeAreaBlockGroup = blockgroups.find(
                  (bg) => bg.id == externalID
              );

              return bgMatch.val * 100;
          };

          const getVolumeAlpha = (externalID: string): number =>
              getVolumePercent(externalID) * 255;

          const mapLayers = new MapLayers(
              buxtonDeck.deck,
              MapTypesEnum.RetailMatchMap
          );
          const layerId: string = isBenchmark ? "benchmark" : "selectedMarker";
          const polygonLayer = mapLayers.createRetailMatchPolygonLayer(
              polygons,
              `deck-retail-match-polygon-layer-${layerId}`,
              getVolumeAlpha
          );

          if (buxtonDeck.getProp(polygonLayer)) {
              buxtonDeck.updateProp(polygonLayer);
          } else {
              buxtonDeck.addProp(polygonLayer, googleMap);
          }
      };

      const loadTradeAreaBlockGroups = async (sid: string) => {
          let blockgroups: IRetailMatchTradeAreaBlockGroup[] = null;

          if (sid === benchmarkRecord.sid) {
              blockgroups = props.jobResult.chartData.siteTradeAreaBlockGroups;
          } else {
              const { tradeAreaBlockGroups }
          = props.jobResult.chartData.storeTradeAreaBlockGroups.find(
              (storeBg) => storeBg.id === sid
          );
              blockgroups = tradeAreaBlockGroups;
          }

          const polygons = await getTradeAreaPolygonsFromBlockGroups(blockgroups);
          loadBlockGroupMapLayers(
              blockgroups,
              polygons,
              sid === benchmarkRecord.sid
          );
      };

      const handleViewLocation = (store: IStoreScoreRecord): void => {
          const marker: google.maps.Marker = currentMapMarkers.find(
              (marker) =>
                  marker.getPosition().lat() === store.lat
          && marker.getPosition().lng() === store.lon
          );
          setSelectedMarker(marker);

          new google.maps.event.trigger(marker, "click");
          googleMap.panTo({ lat: store.lat, lng: store.lon });
      };

      const handleResetSelection = (): void => {
          selectedMarker.setAnimation(null);
          setSelectedMarker(null);
          buxtonDeck.clearProps();
          loadTradeAreaBlockGroups("-1");
          dispatch(
              setMapCenter(
                  new google.maps.LatLng(benchmarkRecord.lat, benchmarkRecord.lon)
              )
          );
      };

      const formatGeoValue = (): string => {
          let formattedGeoValue = props.jobResult?.job?.geoValues[0] || "";

          if (formattedGeoValue === "USA") {
              formattedGeoValue = "the U.S.";
          } else if (props.jobResult?.job?.geoType === "dma_id") {
              const dma = geographies?.find((geo) => {
                  return geo?.id?.toString() === formattedGeoValue;
              });

              formattedGeoValue = `the ${dma?.name} DMA`;
          }
          return formattedGeoValue;
      };

      const targetSiteTitle = (
          <TableTitleWrapper>
              <ReportH4>
                  <GoogleMapsMarker />
                  <TableTitleSpan>Your Target Site</TableTitleSpan>
              </ReportH4>
              <div>Touch Row To View on Map</div>
          </TableTitleWrapper>
      );

      const benchmarkSetTitle = (
          <TableTitleWrapper>
              <ReportH4>
                  <BrandLogoSmall brandLogo={brandLogo} brandName={brandName} />
                  <TableTitleSpan>
                      {overviewRecord?.brandName} Benchmark Set
                  </TableTitleSpan>
              </ReportH4>
              <div>Touch Row To View on Map</div>
          </TableTitleWrapper>
      );

      if (featureFlags.DigitalShare
      && props.renderType === RenderType.DIGITAL_SHARE
      && isOutputSelected(props.app, DigitalShareItemType.LocationsAnalyzed)) {
          registerRef(props.app, DigitalShareItemType.LocationsAnalyzed, ref);

          registerElement(
              props.app,
              DigitalShareItemType.LocationsAnalyzed,
              "",
              <LocationsAnalyzedSharing
                  brand={brand}
                  benchmarkRecord={props.benchmarkRecord}
                  chartData={props.jobResult.chartData}
              />
          );
      }

      return (
          <ReportScrollableSection id="RMLocationsAnalyzed">
              <ReportSectionHeader>
                  <ReportSectionNumber>02</ReportSectionNumber>
                  <ReportH1>Locations Analyzed</ReportH1>
              </ReportSectionHeader>
              <AnimatedReportCard
                  scrollableParentSelector="#RetailMatchReportBody"
                  animateIn="zoomIn"
                  offset={100}
                  animateOnce={true}>
                  <ReportH4>Which locations were selected for comparison?</ReportH4>
                  <p
                      style={{
                          width: "100%",
                          fontSize: "17px",
                          lineHeight: "25px",
                          marginBottom: "40px",
                      }}
                  >
                      {overviewRecord?.sampleCount || 0} similar {brandName} locations in{" "}
                      {formatGeoValue()} were selected to compare against{" "}
                      {props.benchmarkRecord.addr} based on <strong>urban density</strong>{" "}
            and <strong>proximity</strong>.
                  </p>
                  {benchmarkRecord
                  && (windowSize.width < deviceWidth.desktop
                      ? (
                          <StyledTable
                              columns={mobileSingleBenchmarkTableColumns}
                              rowClassName={(store: IStoreScoreRecord) => store.sid}
                              dataSource={addFullAddr([benchmarkRecord])}
                              rowKey={(job: IStoreScoreRecord) => job.sid}
                              pagination={false}
                              title={() => targetSiteTitle}
                              onRow={(store: IStoreScoreRecord) => {
                                  return {
                                      onClick: () => {
                                          handleViewLocation(store);
                                      },
                                  };
                              }}
                          />
                      )
                      : (
                          <StyledTable
                              columns={desktopSingleBenchmarkTableColumns}
                              rowClassName={(store: IStoreScoreRecord) => store.sid}
                              dataSource={addFullAddr([benchmarkRecord])}
                              rowKey={(job: IStoreScoreRecord) => job.sid}
                              pagination={false}
                              title={() => targetSiteTitle}
                          />
                      ))}
                  {storeScores
                  && (windowSize.width < deviceWidth.desktop
                      ? (
                          <StyledTable
                              columns={mobileBenchmarkSetTableColumns}
                              tableLayout="fixed"
                              rowClassName={(store: IStoreScoreRecord) => store.sid}
                              dataSource={storeScores}
                              rowKey={(job: IStoreScoreRecord) => job.sid}
                              title={() => benchmarkSetTitle}
                              scroll={{ y: 340 }}
                              pagination={false}
                              onRow={(store: IStoreScoreRecord) => {
                                  return {
                                      onClick: () => {
                                          handleViewLocation(store);
                                      },
                                  };
                              }}
                          />
                      )
                      : (
                          <StyledTable
                              columns={desktopBenchmarkSetTableColumns}
                              tableLayout="fixed"
                              rowClassName={(store: IStoreScoreRecord) => store.sid}
                              dataSource={storeScores}
                              rowKey={(job: IStoreScoreRecord) => job.sid}
                              title={() => benchmarkSetTitle}
                              scroll={{ y: 340 }}
                              pagination={false}
                          />
                      ))}
                  <ReportH4>Click on location icons to see trade areas.</ReportH4>
                  <p
                      style={{
                          width: "100%",
                          fontSize: "15px",
                          lineHeight: "21px",
                      }}
                  >
            This map shows the target site and its estimated trade area along
            with benchmark locations and their mobile device-based actual trade
            areas.
                  </p>
                  <ReportMapWrapper>
                      <Button
                          title={"Reset Selection"}
                          clickCallback={() => handleResetSelection()}
                          style={{
                              display: "initial",
                              marginTop: 0,
                              position: "absolute",
                              top: "18px",
                              left: "24px",
                              animation: "zoomInLeft",
                              animationDuration: "0.5s",
                              borderRadius: "20px",
                              width: "156px",
                              zIndex: 1,
                              fontSize: "13px",
                              color: "black",
                          }}
                      />
                      <ReportMap
                          map={googleMap}
                          mapDomId={`retail-match-locations-analyzed`}
                          createMapAction={createNewGoogleMap}
                      />
                  </ReportMapWrapper>
                  <RetailMatchMapLegend brandLogo={brandLogo} brandName={brandName} />
              </AnimatedReportCard>
          </ReportScrollableSection>
      );
  };
